import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ref, onValue, update } from 'firebase/database';
import { db } from '../lib/firebase.js';
import { useAuth } from '../lib/useAuth.jsx';
import './AdminCommunities.css';

// AdminCommunities — super-admin only. Lists every community,
// lets the super-admin create new ones.
//
// Multi-admin model (Option A — reverse index, super-admin managed):
//
//   A community can have more than one admin. There are three places
//   admin access is recorded, all written together when a community
//   is created:
//
//     1. contact_email — the PRIMARY admin (kept for backward
//        compatibility and shown as the contact on the roster).
//     2. communities/{code}/admins/{emailKey} — the per-community
//        list of every admin (primary + co-admins). The database
//        rules read this node to authorize roster / account / device
//        / entitlement writes.
//     3. admin_index/{emailKey}/{code} — the reverse index, so an
//        admin can discover which communities they administer without
//        a query. useAdminAccess reads this.
//
//   emailKey is the lowercased email with dots replaced by commas —
//   the only illegal Firebase-key character in a normal email ('@'
//   is allowed). This MUST match emailToKey() in useAdminAccess.jsx
//   and the rules' auth.token.email.replace('.', ',').
//
//   Only the super-admin can add or remove admins (the admin_index
//   and admins nodes are super-admin-write in the rules). Community
//   admins manage residents but not the admin list.
export default function AdminCommunities() {
  const { user, logout } = useAuth();

  const [communities, setCommunities] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    contact_name: '',
    contact_email: '',
    additional_admins: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const communitiesRef = ref(db, 'brain_health/communities');
    const unsubscribe = onValue(
      communitiesRef,
      (snap) => setCommunities(snap.val() || {}),
      (err) => setError(err.message)
    );
    return unsubscribe;
  }, []);

  const sortedCommunities = communities
    ? Object.entries(communities).sort(
        (a, b) => (a[1].name || '').localeCompare(b[1].name || '')
      )
    : [];

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const code = (formData.code || makeCodeFromName(formData.name)).trim();

      if (!code) {
        throw new Error('Community code is required.');
      }
      if (communities && communities[code]) {
        throw new Error(`A community with code "${code}" already exists.`);
      }

      // Client-side email format check. The database rule will also
      // enforce this — having it here gives a clearer error message.
      const email = formData.contact_email.trim().toLowerCase();
      if (!email) {
        throw new Error("Administrator email is required — it's what lets the community's admin sign in.");
      }
      if (!isValidEmail(email)) {
        throw new Error('Please enter a valid email address.');
      }

      const contactName = formData.contact_name.trim();

      // Parse additional admin emails (comma- or newline-separated).
      // Validate each, lowercase, and de-dupe against the primary.
      const extraEmails = parseEmailList(formData.additional_admins);
      for (const a of extraEmails) {
        if (!isValidEmail(a)) {
          throw new Error(`"${a}" is not a valid email address.`);
        }
      }

      // The full admin set: primary first, then any co-admins, deduped.
      const adminEmails = [];
      const seen = new Set();
      for (const a of [email, ...extraEmails]) {
        if (!seen.has(a)) { seen.add(a); adminEmails.push(a); }
      }

      const now = Date.now();

      const record = {
        name: formData.name.trim(),
        contact_admin: contactName,
        contact_email: email,
        notes: formData.notes.trim(),
        created_at: now,
      };
      Object.keys(record).forEach((k) => {
        if (record[k] === '') delete record[k];
      });

      // Build a single atomic multi-location update. Firebase rejects an
      // update() where one path is an ancestor of another, so the per-admin
      // records are nested INTO the community object (a single path) rather than
      // written as separate child paths under it. admin_index lives under a
      // different top-level path, so those stay as their own update entries.
      record.admins = {};
      const updates = {};

      adminEmails.forEach((adminEmail) => {
        const key = emailToKey(adminEmail);
        // The primary admin's display name is the contact name; we
        // don't have names for co-admins, so leave name off for them.
        const entry = { email: adminEmail, added_at: now };
        if (adminEmail === email && contactName) {
          entry.name = contactName;
        }
        record.admins[key] = entry;
        updates[`brain_health/admin_index/${key}/${code}`] = entry;
      });

      updates[`brain_health/communities/${code}`] = record;

      await update(ref(db), updates);

      setFormData({
        name: '', code: '', contact_name: '', contact_email: '',
        additional_admins: '', notes: '',
      });
      setShowForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  function handleField(name, value) {
    setFormData((d) => ({ ...d, [name]: value }));
  }

  return (
    <div className="container admin-communities">
      <div className="admin-bar fade-in-1">
        <span className="eyebrow">Super Administrator</span>
        <button
          type="button"
          className="admin-bar-logout"
          onClick={logout}
        >
          Signed in as {user.email} · Sign out
        </button>
      </div>

      <h1 className="admin-title fade-in-2">
        Brain Health <em>communities</em>.
      </h1>

      <p className="admin-lede fade-in-3">
        Every community subscribed to the Brain Health program. Click a
        community to manage its residents, or add a new one below.
      </p>

      {error ? (
        <p className="admin-error" role="alert">{error}</p>
      ) : null}

      <div className="admin-communities-list fade-in-4">
        {communities === null ? (
          <p className="admin-meta">Loading…</p>
        ) : sortedCommunities.length === 0 ? (
          <div className="admin-empty">
            <p>No communities yet. Add the first one to get started.</p>
          </div>
        ) : (
          <ul className="admin-list">
            {sortedCommunities.map(([code, c]) => (
              <li key={code} className="admin-list-item">
                <Link
                  to={`/admin/communities/${encodeURIComponent(code)}/residents`}
                  className="admin-list-link"
                >
                  <div className="admin-list-main">
                    <span className="admin-list-name">{c.name}</span>
                    <span className="admin-list-code">{code}</span>
                  </div>
                  {c.contact_email ? (
                    <span className="admin-list-meta">
                      Admin: {c.contact_admin || '—'} · {c.contact_email}
                    </span>
                  ) : null}
                  <span className="admin-list-arrow" aria-hidden="true">→</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {!showForm ? (
        <button
          type="button"
          className="btn admin-add-button"
          onClick={() => setShowForm(true)}
        >
          + Add a community
        </button>
      ) : (
        <form onSubmit={handleSubmit} className="admin-form">
          <h2 className="admin-form-title">New community</h2>

          <FormField
            label="Community name *"
            id="name"
            value={formData.name}
            onChange={(v) => handleField('name', v)}
            placeholder="Sunrise Meadows"
            required
            autoFocus
          />

          <FormField
            label="Community code"
            id="code"
            value={formData.code}
            onChange={(v) => handleField('code', v)}
            placeholder={makeCodeFromName(formData.name) || 'sunrise-meadows'}
            hint="Used internally and in URLs. Leave blank to auto-generate from the name."
          />

          <FormField
            label="Primary admin contact (name)"
            id="contact_name"
            value={formData.contact_name}
            onChange={(v) => handleField('contact_name', v)}
            placeholder="Activity Director name"
          />

          <FormField
            label="Administrator email *"
            id="contact_email"
            type="email"
            value={formData.contact_email}
            onChange={(v) => handleField('contact_email', v)}
            placeholder="director@sunrisemeadows.com"
            required
            hint="This email will be able to sign in at brain.quiltt.com/admin/login and manage this community's residents. They use the same passwordless magic-link flow."
          />

          <FormField
            label="Additional admins"
            id="additional_admins"
            multiline
            value={formData.additional_admins}
            onChange={(v) => handleField('additional_admins', v)}
            placeholder="assistant@sunrisemeadows.com, lead@sunrisemeadows.com"
            hint="Optional. One email per line, or separated by commas. Each can sign in and manage this community's residents with the same magic-link flow. You can manage these later from the community's roster page."
          />

          <FormField
            label="Notes"
            id="notes"
            multiline
            value={formData.notes}
            onChange={(v) => handleField('notes', v)}
            placeholder="Address, contract details, anything else worth recording…"
          />

          <div className="admin-form-actions">
            <button type="submit" className="btn" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create community'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => { setShowForm(false); setError(null); }}
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function makeCodeFromName(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

// Email → Firebase key. Dots are the only illegal key character in
// a normal email ('@' is allowed), so we swap dots for commas. This
// MUST match emailToKey() in useAdminAccess.jsx and the rules'
// auth.token.email.replace('.', ',') (which replaces ALL dots).
function emailToKey(email) {
  return String(email || '').toLowerCase().replaceAll('.', ',');
}

function isValidEmail(email) {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(email || '').trim());
}

// Split a comma- or newline-separated list of emails into a clean,
// lowercased, de-duplicated array. Blank entries are dropped.
function parseEmailList(raw) {
  const seen = new Set();
  const out = [];
  String(raw || '')
    .split(/[\n,]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
    .forEach((e) => {
      if (!seen.has(e)) { seen.add(e); out.push(e); }
    });
  return out;
}

function FormField({
  label, id, value, onChange, placeholder,
  type = 'text', required = false, autoFocus = false,
  multiline = false, hint = null,
}) {
  const Tag = multiline ? 'textarea' : 'input';
  return (
    <div className="admin-form-field">
      <label htmlFor={id} className="admin-form-label">{label}</label>
      <Tag
        id={id}
        type={multiline ? undefined : type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        autoFocus={autoFocus}
        rows={multiline ? 3 : undefined}
        className="admin-form-input"
      />
      {hint ? <span className="admin-form-hint">{hint}</span> : null}
    </div>
  );
}
